import { ApolloQueryResult } from 'apollo-client';

import { MAX_ENTITIES_NUMBER_FROM_API } from '../../constants';
import { uaTraitsQuery } from '../../graphql/queries/grid/additionalTargetings';
import { cryptaSegments } from '../../graphql/queries/grid/crypta-segments';
import {
    adGroupsQueryForEdit,
    adGroupsQueryKeywordsAndRetargeting,
    adsInternalQueryForCreation,
    campaignBasicDataForGroupEdit,
    feedsQuery,
    groupKeywordsQuery,
    productsListQuery,
    recommendationsGroupEditQuery,
    smartFeedSchemaQuery
} from '../../graphql/queries/grid/domain';
import { placementsQuery } from '../../graphql/queries/grid/placements';
import {
    retargetingConditionsQueryForGroupEdit,
    retargetingConditionsQueryForUserProfile
} from '../../graphql/queries/grid/retargeting';
import { mapApiResponseToInternalNewBanner } from '../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import { mapUaTraitApiResponseToSelectItems } from '../../mappers/group/group';
import { mapProductsListFromApi } from '../../mappers/products/products-list-mapper';
import { CampaignsCalcTypeFilter, CampaignType } from '../../typings/domain';
import {
    CampaignBasicDataForGroupEdit,
    CryptaSegments,
    CurrencyCode,
    FeedsQuery,
    GdAdGroupOrderByFieldInput,
    GdAdOrderByFieldInput,
    GdBusinessType,
    GdCampaignsContainerInput,
    GdFeedType,
    GdPlacementFilterInput,
    GdRetargetingConditionTypeInput,
    GdShowConditionOrderByFieldInput,
    GdShowConditionTypeInput,
    GdStatPresetInput,
    GdStringDictNameInput,
    GroupKeywordsQuery,
    GroupKeywordsQuery_client_showConditions_rowset,
    GroupsQueryForEdit,
    GroupsQueryKeywordsAndRetargeting,
    GroupsQueryKeywordsAndRetargeting_client_retargetings_rowset_retargetingCondition,
    GroupsQueryKeywordsAndRetargeting_client_showConditions_rowset_GdKeyword,
    InternalBannersQueryForCreation,
    OrderInput,
    PlacementsQuery,
    ProductsListQuery,
    RecommendationsGroupEditQuery,
    RetargetingConditionsQueryForGroupEdit,
    RetargetingConditionsQueryForUserProfile,
    SmartFeedSchemaQuery,
    UaTraitsQuery
} from '../../typings/graphql-grid';
import { GroupId, StringDictsItems } from '../../typings/groups';
import { InternalBannersEditData } from '../../typings/internalBanners';
import { StringDicts } from '../../typings/state';
import { BaseDataProvider } from '../BaseDataProvider/BaseDataProvider';
import { mapToSmartFiltersApiInput } from '../smartFilters/SmartFiltersDataProvider';

const mapToCampaignsApiInput = (
    campaignId: string
): GdCampaignsContainerInput => {
    return {
        filter: {
            campaignIdIn: [campaignId]
        },
        statRequirements: { preset: GdStatPresetInput.TODAY },
        limitOffset: {
            offset: 0,
            limit: 1
        },
        orderBy: []
    };
};

const mapToGroupApiInput = (
    groupsIds: Array<GroupId>,
    campaignsIds: Array<string>
) => {
    return {
        filter: {
            adGroupIdIn: groupsIds.map(item => Number(item)),
            campaignIdIn: campaignsIds.map(item => Number(item))
        },
        statRequirements: { preset: 'TODAY' },
        limitOffset: {
            offset: 0,
            limit: 10000
        },
        orderBy: {
            field: GdAdOrderByFieldInput.ID,
            order: OrderInput.ASC
        }
    };
};

const mapToConditionsApiInput = (
    groupsIds: Array<GroupId>,
    campaignsIds: Array<string>
) => {
    return {
        limitOffset: {
            offset: 0,
            limit: 10000
        },
        statRequirements: { preset: 'TODAY' },
        orderBy: {
            order: OrderInput.DESC,
            field: GdShowConditionOrderByFieldInput.GROUP_ID
        },
        filter: {
            typeIn: [
                GdShowConditionTypeInput.KEYWORD,
                GdShowConditionTypeInput.RELEVANCE_MATCH,
                GdShowConditionTypeInput.FILTERS
            ],
            adGroupIdIn: groupsIds.map(item => Number(item)),
            campaignIdIn: campaignsIds.map(item => Number(item))
        }
    };
};

const mapToAdsApiInput = (
    groupsIds: Array<GroupId>,
    campaignsIds: Array<string>
) => {
    return {
        filter: {
            adGroupIdIn: groupsIds.map(item => Number(item)),
            campaignIdIn: campaignsIds.map(item => Number(item))
        },
        statRequirements: { preset: 'TODAY' },
        limitOffset: {
            offset: 0,
            limit: 10000
        },
        orderBy: {
            field: GdAdOrderByFieldInput.ID,
            order: OrderInput.ASC
        }
    };
};

const mapToRetargetingsApiInput = (
    groupsIds: Array<GroupId>,
    campaignsIds: Array<string>
) => {
    return {
        limitOffset: {
            offset: 0,
            limit: 10000
        },
        statRequirements: { preset: 'TODAY' },
        orderBy: {
            order: OrderInput.DESC,
            field: GdShowConditionOrderByFieldInput.GROUP_ID
        },
        filter: {
            adGroupIdIn: groupsIds.map(item => Number(item)),
            campaignIdIn: campaignsIds.map(item => Number(item))
        }
    };
};

export class GroupsDataProvider extends BaseDataProvider {
    public getGroups = (
        login: string,
        groupsIds: Array<GroupId>,
        campaignsIds: Array<string>
    ) => {
        return this.createQuery<GroupsQueryForEdit>(adGroupsQueryForEdit, {
            variables: {
                adGroupsInput: mapToGroupApiInput(groupsIds, campaignsIds),
                showConditionsInput: mapToConditionsApiInput(
                    groupsIds,
                    campaignsIds
                ),
                smartFiltersInput: mapToSmartFiltersApiInput(
                    campaignsIds,
                    groupsIds
                ),
                retargetingsInput: mapToRetargetingsApiInput(
                    groupsIds,
                    campaignsIds
                ),
                login
            }
        });
    };

    public getKeywordsAndRetargetings = (
        login: string,
        groupsIds: Array<GroupId>,
        campaignsIds: Array<string>
    ) => {
        const query = this.createQuery<GroupsQueryKeywordsAndRetargeting>(
            adGroupsQueryKeywordsAndRetargeting,
            {
                variables: {
                    showConditionsInput: mapToConditionsApiInput(
                        groupsIds,
                        campaignsIds
                    ),
                    retargetingsInput: mapToRetargetingsApiInput(
                        groupsIds,
                        campaignsIds
                    ),
                    login
                }
            }
        );

        return query.then(
            (res: ApolloQueryResult<GroupsQueryKeywordsAndRetargeting>) => {
                const keywords = res.data.client.showConditions.rowset
                    .filter(item => item.__typename === 'GdKeyword')
                    .map(
                        item =>
                            (item as GroupsQueryKeywordsAndRetargeting_client_showConditions_rowset_GdKeyword)
                                .keyword
                    );

                const retargeting = res.data.client.retargetings.rowset
                    .filter(
                        item =>
                            item.retargetingCondition &&
                            item.retargetingCondition.__typename ===
                                'GdRetargetingCondition'
                    )
                    .map(
                        item =>
                            (item.retargetingCondition as GroupsQueryKeywordsAndRetargeting_client_retargetings_rowset_retargetingCondition)
                                .name
                    );

                return {
                    keywords,
                    retargeting
                };
            }
        );
    };

    public getCryptaSegments = (
        name: string
    ): Promise<ApolloQueryResult<CryptaSegments>> => {
        return this.client.query<CryptaSegments>({
            query: cryptaSegments,
            variables: {
                input: {
                    filter: {
                        name: name
                    }
                }
            }
        });
    };

    public getPlacements = (
        input: GdPlacementFilterInput
    ): Promise<ApolloQueryResult<PlacementsQuery>> => {
        return this.client.query<PlacementsQuery>({
            query: placementsQuery,
            variables: { input }
        });
    };

    public getProducts(
        calcType: CampaignsCalcTypeFilter,
        type: CampaignType,
        currency: CurrencyCode
    ) {
        const query = this.createQuery<ProductsListQuery>(productsListQuery, {
            variables: {
                productFilterInput: {
                    filter: {
                        calcType: calcType,
                        type: type,
                        currency: currency
                    }
                }
            }
        });

        return query.then(mapProductsListFromApi);
    }

    public getRetargetingConditionsForCpm = (
        login: string,
        retargetingConditionIds: Array<string>
    ): Promise<ApolloQueryResult<RetargetingConditionsQueryForUserProfile>> => {
        const query = this.createQuery<
            RetargetingConditionsQueryForUserProfile
        >(retargetingConditionsQueryForUserProfile, {
            variables: {
                retargetingConditionsInput: {
                    filter: {
                        retargetingConditionIdIn: retargetingConditionIds
                    }
                },
                login
            }
        });

        return query;
    };

    public getRetargetingConditions = (login: string) => {
        const query = this.createQuery<RetargetingConditionsQueryForGroupEdit>(
            retargetingConditionsQueryForGroupEdit,
            {
                variables: {
                    retargetingConditionsInput: {
                        filter: {
                            retargetingConditionTypeIn: [
                                GdRetargetingConditionTypeInput.METRIKA_GOALS
                            ],
                            interest: false
                        }
                    },
                    login
                }
            }
        );

        return query;
    };

    public getSmartFeedSchema = (
        businessType: GdBusinessType,
        feedType?: GdFeedType
    ) => {
        return this.createQuery<SmartFeedSchemaQuery>(smartFeedSchemaQuery, {
            variables: {
                input: {
                    feedType,
                    businessType
                }
            },
            fetchPolicy: 'cache-first'
        });
    };

    public getFeeds(login: string) {
        return this.createQuery<FeedsQuery>(feedsQuery, {
            variables: {
                login
            }
        });
    }

    public getCampaignBasicDataForGroupEdit = (
        login: string,
        campaignId: string
    ) => {
        const query = this.createQuery<CampaignBasicDataForGroupEdit>(
            campaignBasicDataForGroupEdit,
            {
                variables: {
                    login,
                    campInput: mapToCampaignsApiInput(campaignId)
                }
            }
        );

        return query;
    };

    public getGroupKeywordsList = (
        login: string,
        campaignIdIn: string,
        adGroupIdIn: string
    ): Promise<string[]> => {
        const query = this.createQuery<GroupKeywordsQuery>(groupKeywordsQuery, {
            variables: {
                login: login,
                input: {
                    orderBy: [
                        {
                            field: GdAdGroupOrderByFieldInput.ID,
                            order: OrderInput.ASC
                        }
                    ],
                    filter: {
                        campaignIdIn: [campaignIdIn],
                        adGroupIdIn: [adGroupIdIn],
                        typeIn: GdShowConditionTypeInput.KEYWORD
                    },
                    limitOffset: {
                        offset: 0,
                        limit: MAX_ENTITIES_NUMBER_FROM_API
                    },
                    statRequirements: {
                        preset: GdStatPresetInput.TODAY
                    }
                }
            }
        });

        return query.then((result: ApolloQueryResult<GroupKeywordsQuery>) => {
            return result.data.client.showConditions.rowset.reduce(
                (
                    acc: Array<string>,
                    item: GroupKeywordsQuery_client_showConditions_rowset
                ) => {
                    if (item.__typename === 'GdKeyword') {
                        acc.push(item.keyword);
                    }

                    return acc;
                },
                []
            );
        });
    };

    public getGroupBasicDataForInternalBannerCreation = (
        login: string,
        groupId: string,
        campaignId: string
    ): Promise<InternalBannersEditData> => {
        const query = this.createQuery<InternalBannersQueryForCreation>(
            adsInternalQueryForCreation,
            {
                variables: {
                    adGroupsInput: mapToAdsApiInput([groupId], [campaignId]),
                    login
                }
            }
        );

        return query.then(mapApiResponseToInternalNewBanner);
    };

    public getUaTraits(
        stringDicts?: StringDicts,
        isInternalGroup?: boolean
    ): Promise<StringDictsItems> {
        if (stringDicts) {
            return Promise.resolve(stringDicts);
        }

        if (!isInternalGroup) {
            return Promise.resolve({});
        }

        return this.createQuery<UaTraitsQuery>(uaTraitsQuery, {
            variables: {
                dictNames: [
                    GdStringDictNameInput.UA_TRAITS_OS_NAME,
                    GdStringDictNameInput.UA_TRAITS_BROWSER_ENGINE,
                    GdStringDictNameInput.UA_TRAITS_BROWSER_NAME,
                    GdStringDictNameInput.UA_TRAITS_DEVICE_VENDOR,
                    GdStringDictNameInput.UA_TRAITS_OS_FAMILY
                ]
            }
        }).then(mapUaTraitApiResponseToSelectItems);
    }

    public getGroupEditRecommendations = (
        login: string,
        campaignIds: Array<string>,
        groupIds: Array<string>
    ): Promise<ApolloQueryResult<RecommendationsGroupEditQuery>> => {
        const query = this.createQuery<RecommendationsGroupEditQuery>(
            recommendationsGroupEditQuery,
            {
                variables: {
                    login,
                    adGroupInput: {
                        statRequirements: {
                            preset: GdStatPresetInput.LAST_30DAYS
                        },
                        orderBy: [
                            {
                                field: GdAdGroupOrderByFieldInput.CAMPAIGN_ID,
                                order: OrderInput.DESC
                            }
                        ],
                        limitOffset: {
                            offset: 0,
                            limit: 10000
                        },
                        filter: {
                            campaignIdIn: campaignIds,
                            adGroupIdIn: groupIds
                        }
                    }
                }
            }
        );

        return query;
    };
}
